/**
 * business-config.ts — constants for the Business entity dialog.
 *
 * Source of truth: `docs/port-design/category-cards/business.md` §1-2.
 *
 * Per Bill's 2026-04-23 admin-configurable architecture mandate, these MUST
 * eventually move to SQL catalog tables:
 *   - Business sub-type picker → `EntityTypes` (or similar) SQL catalog
 *   - Fiscal year-end → `LookupValues` catalog
 *   - NAICS code picker → `NaicsCodes` SQL catalog (per people-form-port.md §6)
 *
 * For v0.1.37 these are hardcoded here; port phase 2 moves them behind the
 * `useConfig()` bundle in `/v3/config/*` responses.
 */

// -----------------------------------------------------------------------------
// Entity sub-types (Business group — 9 values per business.md §1)
// -----------------------------------------------------------------------------

export interface BusinessSubTypeOption {
  /**
   * SPFx short form value. NOTE: the shared-types `CustomerType` enum uses
   * long forms for some of these (`Corporation`, `NonProfit`). The dispatcher
   * `findGroupForType.ts` already aliases both to the `business` group, so we
   * surface the SHORT form in the picker where shared-types has no long-form
   * equivalent (`IOLTA`, `Political`, `UnincorpAssoc`, `ICS_Business`) and
   * fall back to the enum long form where it exists.
   *
   * TODO(bill): reconcile short vs long form — pick one canonical shape for
   * `RaoPerson.customerType` + `/v3/config/document-requirements?entityType=X`
   * and align the seed SQL rows to match. See business.md §"Open questions" #2.
   */
  readonly value: string;
  readonly label: string;
  /** Short helper text shown under the picker when this value is selected. */
  readonly hint?: string;
  /** Call Class (Core Director Field 92 value per business.md §1). */
  readonly callClass?: string;
  /** When true, flip Call Class 20→21 via `isNonProfit=true` on save. */
  readonly isNonProfit?: boolean;
}

export const BUSINESS_SUB_TYPES: ReadonlyArray<BusinessSubTypeOption> = [
  {
    value: 'Corporation',
    label: 'Corporation (Corp)',
    hint: 'For-profit corporation — Articles of Incorporation; Corporate Banking Resolution required.',
    callClass: '20',
  },
  {
    value: 'LLC',
    label: 'Limited Liability Company (LLC)',
    hint: 'Articles of Organization; Operating Agreement required.',
    callClass: '20',
  },
  {
    value: 'DBA',
    label: 'DBA / Sole Proprietor',
    hint: 'Fictitious Name Registration; sole-prop may use personal SSN (TinCode=S).',
    callClass: '25',
  },
  {
    value: 'Partnership',
    label: 'Partnership',
    hint: 'Partnership Authorization Agreement; minimum 2 signers required.',
    callClass: '20',
  },
  {
    value: 'NonProfit',
    label: 'Non-Profit',
    hint: '501(c) determination letter required. Beneficial Ownership uses Controller-only variant.',
    callClass: '21',
    isNonProfit: true,
  },
  {
    value: 'UnincorpAssoc',
    label: 'Unincorporated Association',
    hint: 'Charter / bylaws; Certificate of Authority.',
    callClass: '21',
  },
  {
    value: 'IOLTA',
    label: 'IOLTA (MO Lawyer Trust)',
    hint: 'Fixed EIN 43-1355525; interest redirects to 1058924; NSF notice goes to MO Chief Disciplinary Counsel.',
    callClass: '23',
  },
  {
    value: 'Political',
    label: 'Political Committee',
    hint: 'Statement of Committee Organization (MO Ethics Commission); $50/day cash limit; NAICS=813940.',
    callClass: '28',
  },
  {
    value: 'ICS_Business',
    label: 'ICS Business (Finance Dept Only)',
    hint: 'Finance-department-only entity. Deposit Pro workflow is NOT used.',
    callClass: '29',
  },
];

// Fast-lookup sets -----------------------------------------------------------

export const NONPROFIT_SUBTYPES: ReadonlySet<string> = new Set(['NonProfit']);
export const IOLTA_SUBTYPES: ReadonlySet<string> = new Set(['IOLTA']);
export const POLITICAL_SUBTYPES: ReadonlySet<string> = new Set(['Political']);

/** Sub-types that require supervisor approval on CIF add (seed 11-entity-rules.sql:229-236). */
export const SUPERVISOR_APPROVAL_SUBTYPES: ReadonlySet<string> = new Set([
  'Corporation',
  'LLC',
  'Partnership',
  'NonProfit',
]);

// -----------------------------------------------------------------------------
// SoS state options — same 50-state + DC/territories list as the address
// dropdown, surfaced here so the SoS state picker can default to branch state
// without cross-importing AddressSection.
// -----------------------------------------------------------------------------

export const SOS_STATE_OPTIONS: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

// -----------------------------------------------------------------------------
// Fiscal year-end common values (month-day pairs for dropdown defaults)
// -----------------------------------------------------------------------------

export interface FiscalYearEndOption {
  /** Value stored — `MM-DD` form to align with Core Director date slots. */
  readonly value: string;
  readonly label: string;
}

export const FISCAL_YEAR_END_OPTIONS: ReadonlyArray<FiscalYearEndOption> = [
  { value: '12-31', label: 'December 31 (calendar year)' },
  { value: '06-30', label: 'June 30' },
  { value: '09-30', label: 'September 30' },
  { value: '03-31', label: 'March 31' },
  { value: '01-31', label: 'January 31' },
  { value: '04-30', label: 'April 30' },
  { value: '05-31', label: 'May 31' },
  { value: '07-31', label: 'July 31' },
  { value: '08-31', label: 'August 31' },
  { value: '10-31', label: 'October 31' },
  { value: '11-30', label: 'November 30' },
  { value: '02-28', label: 'February 28 / 29' },
  { value: '',      label: 'Other / not specified' },
];

// -----------------------------------------------------------------------------
// NAICS picker helper text (until NaicsCodes catalog + picker lands)
// -----------------------------------------------------------------------------

export const NAICS_HELPER_TEXT =
  'Enter the 6-digit NAICS code for the primary business activity (Core Director Field 53). ' +
  'Core Director requires NAICS for all business entities. Example: 813940 = Political Organizations, ' +
  '522110 = Commercial Banking, 541110 = Offices of Lawyers.';

// -----------------------------------------------------------------------------
// Fixed values for specialized sub-types
// -----------------------------------------------------------------------------

export const IOLTA_FIXED = {
  /** MO Lawyer Trust Account shared EIN — never editable by officer. */
  taxId: '431355525',
  rateCode: '107',
  interestAcctNumber: '1058924',
  nsfNoticeName: 'MO Chief Disciplinary Counsel',
} as const;

export const POLITICAL_FIXED = {
  /** NAICS for Political Organizations. */
  naicsCode: '813940',
  /** Daily cash-withdrawal limit (for UI banner; enforcement lives in pipeline). */
  dailyCashLimit: 50,
} as const;

// -----------------------------------------------------------------------------
// Primary-ID doc-code mapping (Business entity → fulfilled slot on save).
// TODO(bill): confirm which slot rows ship in EntityRuleRequiredDocs for
// UnincorpAssoc / IOLTA / Political / ICS_Business (missing from seed today —
// see business.md §"Open questions" #1).
// -----------------------------------------------------------------------------

export const PRIMARY_DOC_CODE_BY_SUBTYPE: Record<string, string> = {
  Corporation:   'ART_OF_INC',        // Articles of Incorporation
  NonProfit:     'ART_OF_INC',        // Articles of Incorporation + IRS_501C
  LLC:           'OPERATING_AGREE',   // Articles of Organization / Operating Agreement
  DBA:           'DBA_CERTIFICATE',   // Fictitious Name Reg
  Partnership:   'PARTNERSHIP_AGREE',
  UnincorpAssoc: 'CHARTER_BYLAWS',    // (seed row TBD)
  IOLTA:         'IOLTA_CERT',        // (seed row TBD)
  Political:     'COMMITTEE_ORG',     // (seed row TBD — MO Ethics Commission filing)
  ICS_Business:  'CIP_BUS',           // (seed row TBD)
};
